import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const SESSION_CACHE_KEY_NAME = "player_name_cache";
const SESSION_CACHE_KEY_AVATAR = "player_avatar_cache";
const CACHE_EXPIRY_MS = 6 * 60 * 60 * 1000; // 6 hours

interface CacheEntry {
  name?: string;
  url?: string;
  timestamp: number;
}

interface PlayerData {
  uuid: string;
  name: string | null;
  avatar_url: string | null;
  is_bedrock: boolean;
}

// Session cache helpers
const loadSessionCache = (key: string): Record<string, CacheEntry> => {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return {};
    const data = JSON.parse(cached);
    const now = Date.now();
    const validCache: Record<string, CacheEntry> = {};
    for (const [k, entry] of Object.entries(data)) {
      if (now - (entry as CacheEntry).timestamp < CACHE_EXPIRY_MS) {
        validCache[k] = entry as CacheEntry;
      }
    }
    return validCache;
  } catch {
    return {};
  }
};

const saveToSessionCache = (key: string, entries: Record<string, CacheEntry>): void => {
  try {
    const existing = loadSessionCache(key);
    const merged = { ...existing, ...entries };
    sessionStorage.setItem(key, JSON.stringify(merged));
  } catch {
    // Storage quota exceeded
  }
};

// Get cached names from session
const getCachedNamesFromSession = (uuids: string[]): { cached: Record<string, string>; missing: string[] } => {
  const cache = loadSessionCache(SESSION_CACHE_KEY_NAME);
  const cached: Record<string, string> = {};
  const missing: string[] = [];

  for (const uuid of uuids) {
    const entry = cache[uuid];
    if (entry?.name) {
      cached[uuid] = entry.name;
    } else {
      missing.push(uuid);
    }
  }

  return { cached, missing };
};

// Get cached avatars from session
const getCachedAvatarsFromSession = (uuids: string[], size: number): { cached: Record<string, string>; missing: string[] } => {
  const cache = loadSessionCache(SESSION_CACHE_KEY_AVATAR);
  const cached: Record<string, string> = {};
  const missing: string[] = [];

  for (const uuid of uuids) {
    const key = `${uuid}_${size}`;
    const entry = cache[key];
    if (entry?.url) {
      cached[uuid] = entry.url;
    } else {
      missing.push(uuid);
    }
  }

  return { cached, missing };
};

// Batch fetch from Supabase using optimized function
const fetchFromBackendBatch = async (uuids: string[]): Promise<Map<string, PlayerData>> => {
  if (uuids.length === 0) return new Map();

  try {
    // Try using optimized RPC function first
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_gameusers_batch', { p_uuids: uuids });

    if (!rpcError && rpcData && rpcData.length > 0) {
      const sixHoursAgo = new Date(Date.now() - CACHE_EXPIRY_MS);
      const result = new Map<string, PlayerData>();

      for (const user of rpcData) {
        const updatedAt = new Date(user.updated_at);
        if (updatedAt > sixHoursAgo && user.name) {
          result.set(user.uuid, {
            uuid: user.uuid,
            name: user.name,
            avatar_url: user.avatar_url,
            is_bedrock: user.is_bedrock,
          });
        }
      }

      return result;
    }

    // Fallback to direct query
    const { data, error } = await (supabase as any)
      .from('api_gameusers')
      .select('uuid, name, avatar_url, is_bedrock, updated_at')
      .in('uuid', uuids);

    if (error || !data) return new Map();

    const sixHoursAgo = new Date(Date.now() - CACHE_EXPIRY_MS);
    const result = new Map<string, PlayerData>();

    for (const user of data) {
      const updatedAt = new Date(user.updated_at);
      if (updatedAt > sixHoursAgo && user.name) {
        result.set(user.uuid, {
          uuid: user.uuid,
          name: user.name,
          avatar_url: user.avatar_url,
          is_bedrock: user.is_bedrock,
        });
      }
    }

    return result;
  } catch {
    return new Map();
  }
};

// Batch fetch from Edge Function
const fetchFromEdgeFunctionBatch = async (uuids: string[], size: number): Promise<PlayerData[]> => {
  if (uuids.length === 0) return [];

  try {
    const { data, error } = await supabase.functions.invoke('gameuser-cache', {
      body: { uuids, size }
    });

    if (error || !data?.results) return [];
    return data.results;
  } catch {
    return [];
  }
};

// Main batch fetch function
export const fetchPlayerDataBatch = async (
  uuids: string[],
  size: number = 16
): Promise<Record<string, PlayerData>> => {
  const uniqueUuids = [...new Set(uuids)];
  if (uniqueUuids.length === 0) return {};

  const results: Record<string, PlayerData> = {};

  // 1. Check session cache first
  const { cached: cachedNames, missing: missingFromSession } = getCachedNamesFromSession(uniqueUuids);
  const { cached: cachedAvatars } = getCachedAvatarsFromSession(uniqueUuids, size);

  // Add session cached data to results
  for (const uuid of uniqueUuids) {
    if (cachedNames[uuid]) {
      results[uuid] = {
        uuid,
        name: cachedNames[uuid],
        avatar_url: cachedAvatars[uuid] || null,
        is_bedrock: uuid.startsWith("00000000-0000-0000-0009"),
      };
    }
  }

  if (missingFromSession.length === 0) {
    return results;
  }

  // 2. Check Supabase backend cache
  const backendData = await fetchFromBackendBatch(missingFromSession);
  const stillMissing: string[] = [];

  const nameEntries: Record<string, CacheEntry> = {};
  const avatarEntries: Record<string, CacheEntry> = {};

  for (const uuid of missingFromSession) {
    const data = backendData.get(uuid);
    if (data) {
      results[uuid] = data;
      nameEntries[uuid] = { name: data.name || undefined, timestamp: Date.now() };
      if (data.avatar_url) {
        avatarEntries[`${uuid}_${size}`] = { url: data.avatar_url, timestamp: Date.now() };
      }
    } else {
      stillMissing.push(uuid);
    }
  }

  // Save backend hits to session cache
  if (Object.keys(nameEntries).length > 0) {
    saveToSessionCache(SESSION_CACHE_KEY_NAME, nameEntries);
  }
  if (Object.keys(avatarEntries).length > 0) {
    saveToSessionCache(SESSION_CACHE_KEY_AVATAR, avatarEntries);
  }

  // 3. Fetch remaining from Edge Function
  if (stillMissing.length > 0) {
    const edgeResults = await fetchFromEdgeFunctionBatch(stillMissing, size);
    
    const newNameEntries: Record<string, CacheEntry> = {};
    const newAvatarEntries: Record<string, CacheEntry> = {};

    for (const data of edgeResults) {
      results[data.uuid] = data;
      if (data.name) {
        newNameEntries[data.uuid] = { name: data.name, timestamp: Date.now() };
      }
      if (data.avatar_url) {
        newAvatarEntries[`${data.uuid}_${size}`] = { url: data.avatar_url, timestamp: Date.now() };
      }
    }

    if (Object.keys(newNameEntries).length > 0) {
      saveToSessionCache(SESSION_CACHE_KEY_NAME, newNameEntries);
    }
    if (Object.keys(newAvatarEntries).length > 0) {
      saveToSessionCache(SESSION_CACHE_KEY_AVATAR, newAvatarEntries);
    }
  }

  return results;
};

// Hook to prefetch player data for a list of UUIDs
export const usePrefetchPlayerData = (uuids: string[], size: number = 16) => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['playerDataBatch', uuids.sort().join(','), size],
    queryFn: async () => {
      const results = await fetchPlayerDataBatch(uuids, size);
      
      // Populate individual query caches
      for (const [uuid, data] of Object.entries(results)) {
        if (data.name) {
          queryClient.setQueryData(['playerName', uuid], data.name);
        }
        if (data.avatar_url) {
          queryClient.setQueryData(['playerAvatar', `${uuid}_${size}`], data.avatar_url);
        }
      }
      
      return results;
    },
    staleTime: CACHE_EXPIRY_MS,
    gcTime: CACHE_EXPIRY_MS,
    enabled: uuids.length > 0,
    refetchOnWindowFocus: false,
  });
};
